import { ProjectSuggestions } from '@platform/client';
import { Modal, ModalCallToAction, Spinner } from '@platform/components';
import { useState } from 'react';
import { GreenCircleCheckmark } from '../../../svg/GreenCircleCheckmark';
import { useQueryClient } from '@tanstack/react-query';

export const SuggestProjectModal = ({
  organizationId,
  projectSuggestionId,
  projectSuggestionIndex,
  isOpen,
  onClose,
}: {
  organizationId: number;
  projectSuggestionId: number;
  projectSuggestionIndex: number;
  isOpen: boolean;
  onClose: () => void;
}) => {
  const [modalStage, setModalStage] = useState<'confirm' | 'sending' | 'success' | 'error'>(
    'confirm',
  );
  const queryClient = useQueryClient();
  const suggestProjectToRep = ProjectSuggestions.useSendProjectSuggestEmail({
    mutation: {
      onMutate: () => setModalStage('sending'),
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: ProjectSuggestions.getGetProjectSuggestionsQueryKey(organizationId),
        });
        setModalStage('success');
      },
      onError: () => setModalStage('error'),
    },
  });
  const handleSendEmail = () => {
    suggestProjectToRep.mutate({ suggestedProjectId: projectSuggestionId });
  };
  const primaryCta: ModalCallToAction = {
    text: 'Yes, Suggest Project',
    callback: handleSendEmail,
    isDisabled: () => modalStage === 'sending',
  };
  if (modalStage === 'success' || modalStage === 'error') {
    primaryCta.text = 'Close';
    primaryCta.callback = onClose;
  }
  const secondaryCta: ModalCallToAction | undefined =
    modalStage === 'confirm' ?
      {
        text: 'No, Cancel',
        callback: onClose,
      }
    : undefined;

  return (
    <Modal
      headerDivision='divider'
      isOpen={isOpen}
      onClose={onClose}
      title={`Suggest Project #${projectSuggestionIndex + 1}?`}
      variant='dialog'
      primaryCTA={primaryCta}
      secondaryCTA={secondaryCta}
      ignoreExtraPadding
    >
      {modalStage === 'confirm' && (
        <p>Are you sure you want to suggest this project to the organization?</p>
      )}
      {modalStage === 'sending' && (
        <div className='flex justify-center items-center'>
          <Spinner />
        </div>
      )}
      {modalStage === 'error' && (
        <p className='text-red-500'>
          There was an error suggesting this project. Please try again.
        </p>
      )}
      {modalStage === 'success' && (
        <div className='flex justify-center items-center'>
          <GreenCircleCheckmark />
          <p>Project successfully suggested to rep!</p>
        </div>
      )}
    </Modal>
  );
};
